import * as cryptoJs from "crypto-js";
import { AppConfigService } from './app-config.service';
import { StorageService } from './storage.service';
import { Constants } from '../models/constants.model';
import { MessageType } from '../models/message';
import { MessageService } from './message.service';
import { UrlHelper } from '../utils/url-helper';
import { SpinnerService } from './spinner.service';
import { StringHelper } from '../utils/string-helper';
import { HtmlHelper } from '../utils/html-helper';
import * as i0 from "@angular/core";
import * as i1 from "./storage.service";
import * as i2 from "./message.service";
import * as i3 from "./spinner.service";
var AuthorizationService = /** @class */ (function () {
    function AuthorizationService(storage, messages, spinner) {
        this.storage = storage;
        this.messages = messages;
        this.spinner = spinner;
        this.oauthUrl = UrlHelper.trimUrl(AppConfigService.settings.oauthUrl);
    }
    /**
     * Returns true if the client has a valid token
     *
     * @returns {boolean}
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.isAuthorized = function () {
        var expire = this.storage.getTokenExpire();
        return this.storage.getLoginToken() !== null && expire != null && expire > Math.round(new Date().getTime() / 1000);
    };
    /**
     * Invoke login-process
     *
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.logIn = function () {
        if (AuthorizationService.loginInProgress)
            return;
        AuthorizationService.loginInProgress = true;
        var codeChallenge = this.beforeLogin();
        window.location.href = this.getLoginRedirectUri(codeChallenge);
    };
    /**
     * Invoke anonymous login-process
     *
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.logInAnonymous = function () {
        this.beforeLogin(false);
        window.location.href = this.getAnonymousRedirectUri();
    };
    /**
     * Invoke token refresh by form post
     *
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.refreshToken = function (cultureInfo) {
        if (cultureInfo === void 0) { cultureInfo = null; }
        var codeChallenge = this.beforeLogin();
        var formAction = AppConfigService.oauthUrl + '/Authorization/RefreshPublic';
        var htmlForm = HtmlHelper.createHiddenForm(formAction, [
            { key: "RefreshToken", value: this.storage.getRefreshToken() },
            { key: "CultureInfo", value: cultureInfo ? cultureInfo : this.storage.getUiCulture() },
            { key: "ApplicationScope", value: AppConfigService.settings.appScope },
            { key: "moduleRedirectUrl", value: AppConfigService.settings.webBaseUrl },
            { key: Constants.RequestBodyParams.ResponseType, value: "code" },
            { key: Constants.RequestBodyParams.CodeChallenge, value: codeChallenge },
            { key: Constants.RequestBodyParams.CodeChallengeMethod, value: "S256" }
        ]);
        HtmlHelper.appendToBody(htmlForm);
        htmlForm.submit();
    };
    /**
     * Invoke logout-precess
     *
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.logOut = function () {
        var token = this.storage.getLoginToken();
        this.beforeLogout();
        var formAction = AppConfigService.oauthUrl + '/Authorization/' + AppConfigService.settings.instanceId + '/Logout';
        var htmlForm = HtmlHelper.createHiddenForm(formAction, [{ key: Constants.RequestBodyParams.AccessToken, value: token }]);
        HtmlHelper.appendToBody(htmlForm);
        htmlForm.submit();
    };
    /**
     * Process redirect after authorization on OAuth server
     *
     * @param {OAuthToken} oAuthToken
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.safeLoginAndRedirect = function (oAuthToken) {
        // save tokens
        this.storage.setLoginToken(oAuthToken.access_token);
        this.storage.setRefreshToken(oAuthToken.refresh_token);
        this.storage.setTokenExpire(oAuthToken.getExpirationMilliseconds());
        // redirect to pre-login uri
        var redirect = this.storage.getEntryPointUrl();
        if (redirect !== null) {
            this.storage.setEntryPointUrl(null);
            window.location.href = redirect;
        }
        else {
            window.location.href = AppConfigService.settings.webBaseUrl;
        }
    };
    /**
     * Compose value for http-header 'CLX-Authorization'
     *
     * @returns {string}
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.getAuthorizationHeaderValue = function () {
        var tokenType = Constants.UrlParams.TokenType + '=' + AppConfigService.settings.tokenType;
        var accessToken = Constants.UrlParams.AccessToken + '=' + this.storage.getLoginToken();
        return [tokenType, accessToken].join(', ');
    };
    /**
     * Returns the url parameters for Authorization Code Flow with PKCE
     *
     * @private
     * @param {string} codeChallenge
     * @return {string}
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.getAuthCodeArgs = function (codeChallenge) {
        return "response_type=code&code_challenge_method=S256&code_challenge=" + codeChallenge;
    };
    /**
     * Compose OAuth-login uri
     *
     * @private
     * @param {string} codeChallenge
     * @returns {string}
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.getLoginRedirectUri = function (codeChallenge) {
        return this.oauthUrl
            + '/Authorization/'
            + AppConfigService.settings.instanceId
            + '/Login?clientId=' + AppConfigService.settings.clientId
            + '&redirectUrl=' + encodeURIComponent(AppConfigService.settings.webBaseUrl)
            + '&culture_info=' + this.storage.getUiCulture()
            + '&application_scope=' + AppConfigService.settings.appScope
            + '&' + this.getAuthCodeArgs(codeChallenge);
    };
    /**
     * Compose OAuth-login uri for anonymous token
     *
     * @private
     * @returns {string}
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.getAnonymousRedirectUri = function () {
        if (AppConfigService.settings.appScope !== 'Public') {
            var msg = 'appconfig.json: appScope "Public" is required to use anonymous login';
            this.messages.add(msg, MessageType.Error);
            throw new Error(msg);
        }
        return this.oauthUrl
            + '/Authorization/'
            + AppConfigService.settings.instanceId
            + '/Token?clientId=' + AppConfigService.settings.clientId
            + '&redirectUrl=' + encodeURIComponent(AppConfigService.settings.webBaseUrl)
            + '&culture_info=' + this.storage.getUiCulture();
    };
    /**
     * Handle login-prerequisits
     *
     * @private
     * @param {boolean}
     * @returns {string|null} PKCE code_challenge
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.beforeLogin = function (usePkce) {
        if (usePkce === void 0) { usePkce = true; }
        this.spinner.waitFor(new Promise(function () { }));
        // save current uri to redirect after login (only if empty)
        if (!this.storage.getEntryPointUrl()) {
            this.storage.setEntryPointUrl(window.location.href);
        }
        if (!usePkce)
            return null;
        //PKCE
        var code_verifier = this.createCodeVerifier();
        this.storage.setPkceCodeVerifier(code_verifier);
        return this.createSha256CodeChallenge(code_verifier);
    };
    /**
     * Handle logout-prerequisits
     *
     * @private
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.beforeLogout = function () {
        this.storage.clearAll();
    };
    /**
     * Creates a random code_verifier
     *
     * @private
     * @return {string}
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.createCodeVerifier = function () {
        var bytes = new Uint8Array(48);
        window.crypto.getRandomValues(bytes);
        return StringHelper.base64UrlEncodeBytes(bytes);
    };
    /**
     * Creates a SHA256 hash from given codeVerifier
     *
     * @private
     * @param {string} codeVerifier
     * @return {string} base64url encoded sha256 hash
     * @memberof AuthorizationService
     */
    AuthorizationService.prototype.createSha256CodeChallenge = function (codeVerifier) {
        var bytes = cryptoJs.SHA256(codeVerifier);
        var base64String = cryptoJs.enc.Base64.stringify(bytes);
        return StringHelper.convertBase64ToBase64Url(base64String);
    };
    AuthorizationService.loginInProgress = false;
    AuthorizationService.ngInjectableDef = i0.defineInjectable({ factory: function AuthorizationService_Factory() { return new AuthorizationService(i0.inject(i1.StorageService), i0.inject(i2.MessageService), i0.inject(i3.SpinnerService)); }, token: AuthorizationService, providedIn: "root" });
    return AuthorizationService;
}());
export { AuthorizationService };
